import { useMemo, useState } from 'react';
import { useSearch, type useSearchOptions } from '../../hooks';

export interface UseRulesManagerFiltersProps<T, K> {
  initialFilter: T;
  dataSet: K[];
  filterFunc: (item: K, filter: T) => void;
  searchKeys: useSearchOptions<K>['searchKeys'];
}

export function useRulesManagerFilters<Filter extends string, RuleType>({
  initialFilter,
  dataSet,
  searchKeys,
  filterFunc,
}: UseRulesManagerFiltersProps<Filter, RuleType>) {
  const [filter, setFilter] = useState<Filter>(initialFilter);

  const searchOptions = useMemo(
    () => ({
      searchKeys,
      dataSet,
      options: {
        useExtendedSearch: true,
        ignoreLocation: true,
        threshold: 0.0,
      },
    }),
    [dataSet, searchKeys]
  );

  const {
    results: searchedRules,
    setSearchValue,
    searchValue,
  } = useSearch<RuleType>(searchOptions);

  const filteredList = useMemo(
    () => searchedRules.filter((item) => filterFunc(item, filter)),
    [searchedRules, filter, filterFunc]
  );

  return {
    filteredList,
    filter,
    setFilter,
    setSearchValue,
    searchValue,
    isFiltersActive: filter !== initialFilter || !!searchValue?.length,
  };
}
